// What each Otto release tells the user it changed. FTO-128 (the mechanism) / FTO-130 (this content).
//
// This registry lives in the FRONTEND, beside `version.js`, and that is a deliberate choice rather than a
// convenience. Otto's two halves deploy independently — the frontend ships by cherry-pick onto a release
// branch, filter-ai deploys on its own — so in any given environment the two can be at different Otto
// versions (the platform-QA "first ask bad, second good" bug was exactly that skew). A deck served by the
// backend would therefore announce features the running UI does not have. A deck compiled into the bundle
// can only ever describe the code it shipped with.
//
// ---------------------------------------------------------------------------------------------------
// ADDING A RELEASE'S NEWS
//
//   1. Add an entry below with the version `version.js` is about to carry, `status: "draft"`.
//   2. Write 3-5 slides. One heading, one or two sentences, in the user's words — what they can now do and
//      where to click, never the ticket's words. `alt` is required: it is what a reduced-motion viewer
//      gets instead of the motion.
//   3. Record a clip per slide — add a shot to ./recording/clips.record.js, then:
//        npx playwright test -c src/components/filterAssistant/news/recording/record.config.js
//        node src/components/filterAssistant/news/recording/encode.js
//      Review the output, copy the chosen .webm and -poster.png into ./media/, and import them below.
//      Full runbook: .claude/skills/otto-news/SKILL.md
//   4. Flip `status` to "published". Until then the entry is a DRAFT and no user sees it, so the
//      mechanism can ship long before the clips are recorded.
//
// A backend-only release with nothing to show goes in SILENT_VERSIONS instead. One of the two is required:
// `ottoNews.test.js` fails the build when the current OTTO_VERSION appears in neither, so a release author
// has to decide whether there is news rather than silently shipping none.
// ---------------------------------------------------------------------------------------------------
import pagesClip from "./media/otto-pages.webm";
import pagesPoster from "./media/otto-pages-poster.png";
import actionsClip from "./media/otto-actions.webm";
import actionsPoster from "./media/otto-actions-poster.png";
import tableClip from "./media/otto-table.webm";
import tablePoster from "./media/otto-table-poster.png";

// Versions that moved the number without changing anything a user can see. Listing one is an explicit
// "there is no news here", which is why the completeness check accepts it.
export const SILENT_VERSIONS = [
    "1.5.1", // multi-pod backend + a page registration; no visible behaviour change
    // 1.7.0 (FTO-145) IS visible — but not to this deck's audience. The Recommendation Condition Vocabulary settings
    // pages are a permission-gated curator surface in Project Settings, and nothing about the chat changes.
    // Same call that cut the QA/QC slide from 1.6.0 below: curator-facing, so it is not news in the panel.
    "1.7.0"
];

// Every 1.6.0 clip is a CAMERA shot (recording/lib/camera.js): the recorder pushes in on the control being
// used and follows the action, so the recorded viewport is already the composed frame — 1120x700, 16:10.
// The modal reads that aspect and lays the deck out STACKED (clip above the copy). A portrait clip framed
// on the panel alone would sit BESIDE the copy instead; both layouts ship, and the clip decides which. See
// NewsModal.jsx. The camera's tightest framing shows ~450px of page in a 560px pane — 1.24x life size.
//
// Recorded against DEV3, not a local dev server, so the clips show the build a user will actually meet.
// That is only correct once the behaviour being filmed is deployed there: these were shot after FTO-131
// and FTO-132 reached dev3, and filming before that would have put both bugs back into the deck.
const CAMERA_CLIP = { w: 1120, h: 700 };

export const NEWS = [
    {
        version: "1.6.0",
        date: "September 2026",
        status: "published",
        slides: [
            {
                heading: "Otto is on more pages",
                body:
                    "Regions, Sites and Buildings, across both the project and asset hubs — plus the Asset Survey " +
                    "Projects grid. Ask for what you want and Otto filters the page you're on.",
                alt: "Otto opening on the Buildings page and filtering to buildings built before 1990",
                media: { src: pagesClip, poster: pagesPoster, ...CAMERA_CLIP }
            },
            {
                heading: "Ask what Otto can do here",
                body:
                    "Tap Actions in the composer. Otto lists only what this page can really deliver, so it never " +
                    "offers something it would have to turn down.",
                alt: "The Actions button opening a list of what the current page supports",
                media: { src: actionsClip, poster: actionsPoster, ...CAMERA_CLIP }
            },
            {
                heading: "Work the table you're looking at",
                body: "Sort, filter, hide columns and export to Excel straight from the chat, on every table page.",
                alt: "Otto sorting the Recommendations table by Construction Cost, then the re-sorted table",
                media: { src: tableClip, poster: tablePoster, ...CAMERA_CLIP }
            }
        ]
    }
];

// Two slides were drafted for 1.6.0 and cut. Recorded here so the decision is not re-litigated:
//
//   "QA/QC findings become the recommendation's own issues" (FTO-108/116) — the page is permission-gated
//   and needs a completed run before it shows anything, so there was nothing to film on the recording
//   account, and it is curator-facing rather than chat-facing. The shot is kept, skipped, in
//   clips.record.js; re-enable it once a seeded run exists.
//
//   "Otto stays signed in" (FTO-110) — a real improvement with no visual. A clip of it working is
//   indistinguishable from a clip of nothing happening.
